import * as XLSX from "xlsx-js-style";
import { getOrganizationName } from "./organizationUtils";
import { formatInvoiceAmount } from "./invoiceTableUtils";

const BORDER = {
  top: { style: "thin", color: { rgb: "000000" } },
  bottom: { style: "thin", color: { rgb: "000000" } },
  left: { style: "thin", color: { rgb: "000000" } },
  right: { style: "thin", color: { rgb: "000000" } },
};

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
  fill: { fgColor: { rgb: "568AF7" } },
  alignment: { vertical: "center", horizontal: "center", wrapText: true },
  border: BORDER,
};

const LABEL_STYLE = {
  font: { bold: true, sz: 11 },
  alignment: { vertical: "center", horizontal: "left", wrapText: true },
  border: BORDER,
};

const DATA_STYLE = {
  alignment: { vertical: "center", horizontal: "left", wrapText: true },
  border: BORDER,
};

const CENTER_DATA_STYLE = {
  alignment: { vertical: "center", horizontal: "center", wrapText: true },
  border: BORDER,
};

const ORG_TITLE_STYLE = {
  font: { bold: true, sz: 16 },
  alignment: { vertical: "center", horizontal: "center", wrapText: true },
  border: BORDER,
};

const SUMMARY_STAT_CELL_STYLE = {
  font: { bold: true, sz: 11 },
  fill: { fgColor: { rgb: "EAF2FF" } },
  alignment: { vertical: "center", horizontal: "left", wrapText: false },
  border: BORDER,
};

const OLD_RETURNING_PATIENTS_LABEL = "Old/Returning patients:";

function buildSummaryStatCell(label, value) {
  const displayValue = `${label} ${value}`;
  return {
    type: "stat",
    label,
    value,
    displayValue,
  };
}

function toSummaryCellDisplayValue(cell) {
  if (cell == null) return "";
  if (typeof cell === "string" || typeof cell === "number") return String(cell);
  if (cell.type === "label") return cell.text;
  if (cell.type === "stat") return cell.displayValue;
  return String(cell);
}

function setSummaryOverallCell(ws, row, col, cell) {
  if (typeof cell === "string") {
    setCellStyle(ws, row, col, LABEL_STYLE, cell);
    return;
  }

  if (cell?.type === "stat") {
    const ref = cellRef(col, row);
    // Same size as other summary/label text (sz 11). A large font on these cells
    // made Excel stretch the row and look like a blank gap above the table.
    ws[ref] = {
      t: "s",
      v: cell.displayValue,
      s: {
        ...SUMMARY_STAT_CELL_STYLE,
        alignment: {
          vertical: "center",
          horizontal: "left",
          wrapText: false,
        },
        font: {
          bold: true,
          sz: 11,
          color: { rgb: "000000" },
        },
      },
    };
    return;
  }

  setCellStyle(ws, row, col, LABEL_STYLE, cell?.text || "");
}

function padRow(row, columnCount) {
  const source = Array.isArray(row) ? row : [];
  return Array.from({ length: columnCount }, (_, index) => {
    const value = source[index];
    if (value == null) return "";
    // Keep primitives; stringify dates; drop plain objects (would show as blank junk)
    const valueType = typeof value;
    if (valueType === "string" || valueType === "number" || valueType === "boolean") {
      return value;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    return "";
  });
}

function sanitizeTableRows(tableRows, columnCount) {
  return (Array.isArray(tableRows) ? tableRows : [])
    .map((row) => padRow(row, columnCount))
    .filter((row) =>
      row.some((cell) => String(cell ?? "").trim() !== "")
    );
}

/** Department breakdown only for single-day (daily) exports; skip for monthly/yearly. */
function shouldShowDepartmentBreakdown(fromDate, toDate) {
  if (!fromDate || !toDate) return false;
  return String(fromDate) === String(toDate);
}

const PATIENT_SUMMARY_CONFIG = {
  columnCount: 4,
  buildOverallRow: (summary) => [
    "Overall Summary",
    buildSummaryStatCell("Total Patients :", summary?.total ?? 0),
    buildSummaryStatCell("New Patients:", summary?.new ?? 0),
    buildSummaryStatCell(OLD_RETURNING_PATIENTS_LABEL, summary?.returning ?? 0),
  ],
  departmentHeaders: ["Department", "Total Patients", "New Patients", "Old/Returning Patients"],
  mapDepartmentRow: (dept) => [dept.department, dept.total, dept.new, dept.returning],
};

const TYPE_BREAKDOWN_SECTION = {
  headers: ["Type", "Total Invoices", "Total Amount"],
  emptyLabel: "No type data",
  getRows: (summary) => summary?.byType || [],
  mapRow: (row) => [
    row.type || row.department || "Other",
    row.total,
    formatInvoiceAmount(row.totalAmount ?? 0),
  ],
};

const INVOICE_SUMMARY_CONFIG = {
  columnCount: 6,
  buildOverallRow: (summary) => [
    "Overall Summary",
    buildSummaryStatCell("Total Invoices :", summary?.total ?? 0),
    buildSummaryStatCell("Total Amount:", formatInvoiceAmount(summary?.totalAmount ?? 0)),
    buildSummaryStatCell("Total Patients :", summary?.totalPatients ?? 0),
    buildSummaryStatCell("New Patients:", summary?.new ?? 0),
    buildSummaryStatCell(OLD_RETURNING_PATIENTS_LABEL, summary?.returning ?? 0),
  ],
  departmentHeaders: ["Department", "Total Invoices", "Total Amount"],
  emptyBreakdownLabel: "No department data",
  getBreakdownRows: (summary) => summary?.byDepartment || [],
  mapDepartmentRow: (dept) => [
    dept.department,
    dept.total,
    formatInvoiceAmount(dept.totalAmount ?? 0),
  ],
  additionalSections: [TYPE_BREAKDOWN_SECTION],
};

/** Expense export: totals + type breakdown only (no patients / departments). */
const EXPENSE_INVOICE_SUMMARY_CONFIG = {
  columnCount: 3,
  buildOverallRow: (summary) => [
    "Overall Summary",
    buildSummaryStatCell("Total Invoices :", summary?.total ?? 0),
    buildSummaryStatCell("Total Amount:", formatInvoiceAmount(summary?.totalAmount ?? 0)),
  ],
  departmentHeaders: ["Type", "Total Invoices", "Total Amount"],
  emptyBreakdownLabel: "No type data",
  getBreakdownRows: (summary) => summary?.byType || [],
  mapDepartmentRow: (row) => [
    row.type || row.department || "Other",
    row.total,
    formatInvoiceAmount(row.totalAmount ?? 0),
  ],
};

const MEDICINE_INVENTORY_SUMMARY_CONFIG = {
  columnCount: 6,
  buildOverallRow: (summary) => [
    "Overall Summary",
    `Total Medicines: ${summary?.totalMedicines ?? 0}`,
    `Total Quantity: ${summary?.totalQuantity ?? 0}`,
    `IN Txns: ${summary?.inTransactions ?? 0} (Qty: ${summary?.inQuantity ?? 0})`,
    `OUT Txns: ${summary?.outTransactions ?? 0} (Qty: ${summary?.outQuantity ?? 0})`,
    `All Transactions: ${summary?.allTransactions ?? 0}`,
  ],
  departmentHeaders: [
    "Category",
    "Medicines",
    "Total Qty",
    "IN Qty",
    "OUT Qty",
    "Transactions",
  ],
  mapDepartmentRow: (dept) => [
    dept.department,
    dept.medicines ?? 0,
    dept.totalQuantity ?? 0,
    dept.inQuantity ?? 0,
    dept.outQuantity ?? 0,
    dept.transactions ?? 0,
  ],
};

const PATH_INVENTORY_SUMMARY_CONFIG = {
  columnCount: 2,
  skipDepartmentBreakdown: true,
  buildOverallRow: (summary) => [
    "Overall Summary",
    `Total Items: ${summary?.totalItems ?? 0} | Total Quantity: ${summary?.totalQuantity ?? 0}`,
  ],
  departmentHeaders: [],
  mapDepartmentRow: () => [],
};

function buildSheetTitle(organizationName, summaryTitle, periodLabel) {
  const org = (organizationName || "").trim();
  const summary = (summaryTitle || "").trim();
  const period = (periodLabel || "").trim();

  let title = "";
  if (org && summary) title = `${org} - ${summary}`;
  else title = org || summary || "";

  if (period && period !== "All time") {
    title = title ? `${title} ${period}` : period;
  }
  return title;
}

function cellRef(col, row) {
  return XLSX.utils.encode_cell({ c: col, r: row });
}

function ensureCell(ws, row, col, value = "") {
  const ref = cellRef(col, row);
  if (!ws[ref]) {
    ws[ref] = { t: "s", v: value };
  } else if (value !== "" && ws[ref].v == null) {
    ws[ref].v = value;
  }
  return ref;
}

function setCellStyle(ws, row, col, style, value) {
  const ref = cellRef(col, row);
  const existingValue = ws[ref]?.v ?? "";
  ensureCell(ws, row, col, value ?? existingValue);
  ws[ref].s = style;
}

function buildSummaryRows({
  organizationName,
  summaryTitle,
  periodLabel,
  summary,
  sheetColumnCount,
  summaryConfig,
}) {
  const summaryColumnCount = summaryConfig.columnCount;
  const rows = [];
  const headerRows = [];
  const summaryStatRows = [];
  const dataOnlyRows = [];
  let titleRowIndex = null;

  const pushRow = (cells, width = sheetColumnCount) => {
    rows.push(padRow(cells, width));
    return rows.length - 1;
  };

  const sheetTitle = buildSheetTitle(organizationName, summaryTitle, periodLabel);
  titleRowIndex = pushRow([sheetTitle], sheetColumnCount);

  const overallRowCells = summaryConfig.buildOverallRow(summary);
  summaryStatRows.push(
    pushRow(overallRowCells.map(toSummaryCellDisplayValue), sheetColumnCount)
  );

  if (!summaryConfig.skipDepartmentBreakdown) {
    headerRows.push(pushRow(summaryConfig.departmentHeaders, sheetColumnCount));

    const breakdownRows =
      typeof summaryConfig.getBreakdownRows === "function"
        ? summaryConfig.getBreakdownRows(summary)
        : summary?.byDepartment || [];
    if (breakdownRows.length === 0) {
      dataOnlyRows.push(
        pushRow(
          [summaryConfig.emptyBreakdownLabel || "No department data"],
          sheetColumnCount
        )
      );
    } else {
      breakdownRows.forEach((dept) => {
        dataOnlyRows.push(pushRow(summaryConfig.mapDepartmentRow(dept), sheetColumnCount));
      });
    }
  }

  (summaryConfig.additionalSections || []).forEach((section) => {
    headerRows.push(pushRow(section.headers, sheetColumnCount));
    const sectionRows =
      typeof section.getRows === "function" ? section.getRows(summary) : [];
    if (sectionRows.length === 0) {
      dataOnlyRows.push(
        pushRow([section.emptyLabel || "No data"], sheetColumnCount)
      );
    } else {
      sectionRows.forEach((row) => {
        dataOnlyRows.push(pushRow(section.mapRow(row), sheetColumnCount));
      });
    }
  });

  return {
    rows,
    headerRows,
    summaryStatRows,
    summaryStatCellData: { [summaryStatRows[0]]: overallRowCells },
    dataOnlyRows,
    titleRowIndex,
    sheetTitle,
    summaryColumnCount,
  };
}

function autoFitColumns(ws, rows, columnCount, options = {}) {
  const skipRowIndexes = new Set(
    Array.isArray(options.skipRowIndexes) ? options.skipRowIndexes : []
  );
  ws["!cols"] = Array.from({ length: columnCount }, (_, colIndex) => {
    let maxLength = 10;
    rows.forEach((rowValues, rowIndex) => {
      if (skipRowIndexes.has(rowIndex)) return;
      const value = rowValues?.[colIndex];
      if (value == null || value === "") return;
      maxLength = Math.max(maxLength, String(value).length + 2);
    });
    Object.keys(ws).forEach((ref) => {
      if (ref.startsWith("!")) return;
      const cell = ws[ref];
      const decoded = XLSX.utils.decode_cell(ref);
      if (decoded.c !== colIndex || cell?.v == null) return;
      if (skipRowIndexes.has(decoded.r)) return;
      maxLength = Math.max(maxLength, String(cell.v).length + 2);
    });
    return { wch: Math.min(maxLength, 45) };
  });
}

function applyFullRowBorders(ws, rowIndex, columnCount, baseStyle = DATA_STYLE) {
  for (let col = 0; col < columnCount; col += 1) {
    const ref = cellRef(col, rowIndex);
    const value = ws[ref]?.v ?? "";
    if (!ws[ref]?.s) {
      setCellStyle(ws, rowIndex, col, baseStyle, value);
      continue;
    }
    if (!ws[ref].s.border) {
      ws[ref].s = { ...ws[ref].s, border: BORDER };
    }
  }
}

function applyTableGridBorders(ws, rowIndices, columnCount) {
  rowIndices.forEach((rowIndex) => {
    applyFullRowBorders(ws, rowIndex, columnCount);
  });
}

async function downloadTableExcelWithSummary({
  filename,
  sheetName,
  organizationName,
  summaryTitle,
  periodLabel,
  summary,
  tableHeaders,
  tableRows,
  summaryConfig,
  extraSheets = [],
}) {
  const buildSheet = ({
    sheetSummaryTitle,
    sheetPeriodLabel,
    sheetSummary,
    sheetTableHeaders,
    sheetTableRows,
    sheetSummaryConfig,
  }) => {
    const tableColumnCount = sheetTableHeaders?.length || 0;
    const sheetColumnCount = Math.max(tableColumnCount, sheetSummaryConfig.columnCount);
    const resolvedOrganizationName = organizationName || getOrganizationName(null) || "";
    const {
      rows,
      headerRows,
      summaryStatRows,
      summaryStatCellData = {},
      dataOnlyRows,
      titleRowIndex,
      sheetTitle,
      summaryColumnCount,
    } = buildSummaryRows({
      organizationName: resolvedOrganizationName,
      summaryTitle: sheetSummaryTitle,
      periodLabel: sheetPeriodLabel,
      summary: sheetSummary,
      sheetColumnCount,
      summaryConfig: sheetSummaryConfig,
    });

    const tableHeaderRowIndex = rows.length;
    rows.push(padRow(sheetTableHeaders, sheetColumnCount));
    headerRows.push(tableHeaderRowIndex);

    const safeTableRows = sanitizeTableRows(sheetTableRows, sheetColumnCount);
    const tableDataStart = rows.length;
    safeTableRows.forEach((values) => {
      rows.push(padRow(values, sheetColumnCount));
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Keep the used range tight so Excel does not insert a large empty gap.
    if (rows.length > 0 && sheetColumnCount > 0) {
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rows.length - 1, c: sheetColumnCount - 1 },
      });
    }
    const lastColumnIndex = sheetColumnCount - 1;
    const merges = [];

    if (titleRowIndex != null) {
      merges.push({
        s: { r: titleRowIndex, c: 0 },
        e: { r: titleRowIndex, c: lastColumnIndex },
      });
    }

    dataOnlyRows.forEach((rowIndex) => {
      const rowValues = rows[rowIndex] || [];
      if (rowValues[0] === "No department data") {
        merges.push({
          s: { r: rowIndex, c: 0 },
          e: { r: rowIndex, c: summaryColumnCount - 1 },
        });
      }
    });

    ws["!merges"] = merges;

    setCellStyle(ws, titleRowIndex, 0, ORG_TITLE_STYLE, sheetTitle);

    summaryStatRows.forEach((rowIndex) => {
      const rowValues = rows[rowIndex] || [];
      const overallCells = summaryStatCellData[rowIndex];
      for (let col = 0; col < sheetColumnCount; col += 1) {
        if (col < summaryColumnCount && overallCells?.[col] != null) {
          setSummaryOverallCell(ws, rowIndex, col, overallCells[col]);
          continue;
        }
        const style = col < summaryColumnCount ? LABEL_STYLE : DATA_STYLE;
        setCellStyle(ws, rowIndex, col, style, rowValues[col] ?? "");
      }
      // Force a normal row height so summary stats never leave a tall blank band.
      if (!ws["!rows"]) ws["!rows"] = [];
      ws["!rows"][rowIndex] = { hpt: 22, hpx: 22 };
    });

    dataOnlyRows.forEach((rowIndex) => {
      const rowValues = rows[rowIndex] || [];
      if (rowValues[0] === "No department data") {
        for (let col = 0; col < sheetColumnCount; col += 1) {
          const value = col === 0 ? "No department data" : "";
          const style = col === 0 ? CENTER_DATA_STYLE : DATA_STYLE;
          setCellStyle(ws, rowIndex, col, style, value);
        }
        return;
      }

      for (let col = 0; col < sheetColumnCount; col += 1) {
        const style = col === 0 ? DATA_STYLE : CENTER_DATA_STYLE;
        setCellStyle(ws, rowIndex, col, style, rowValues[col] ?? "");
      }
    });

    headerRows.forEach((rowIndex) => {
      const rowValues = rows[rowIndex] || [];
      for (let col = 0; col < sheetColumnCount; col += 1) {
        setCellStyle(ws, rowIndex, col, HEADER_STYLE, rowValues[col] ?? "");
      }
    });

    for (let rowIndex = tableDataStart; rowIndex < rows.length; rowIndex += 1) {
      for (let col = 0; col < sheetColumnCount; col += 1) {
        setCellStyle(ws, rowIndex, col, DATA_STYLE, rows[rowIndex]?.[col] ?? "");
      }
    }

    const borderedRowIndices = [
      ...summaryStatRows,
      ...headerRows,
      ...dataOnlyRows,
      ...Array.from(
        { length: rows.length - tableDataStart },
        (_, index) => tableDataStart + index
      ),
    ];
    applyTableGridBorders(ws, borderedRowIndices, sheetColumnCount);
    autoFitColumns(ws, rows, sheetColumnCount);
    // Re-pin used range after styling so Excel does not keep a padded empty gap.
    if (rows.length > 0 && sheetColumnCount > 0) {
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rows.length - 1, c: sheetColumnCount - 1 },
      });
    }
    return ws;
  };

  const workbook = XLSX.utils.book_new();
  const primaryWs = buildSheet({
    sheetSummaryTitle: summaryTitle,
    sheetPeriodLabel: periodLabel,
    sheetSummary: summary,
    sheetTableHeaders: tableHeaders,
    sheetTableRows: tableRows,
    sheetSummaryConfig: summaryConfig,
  });
  XLSX.utils.book_append_sheet(workbook, primaryWs, sheetName || "Sheet1");

  (extraSheets || []).forEach((extra) => {
    if (!extra?.tableHeaders || !extra?.summaryConfig) return;
    const extraWs = buildSheet({
      sheetSummaryTitle: extra.summaryTitle,
      sheetPeriodLabel: extra.periodLabel || periodLabel,
      sheetSummary: extra.summary,
      sheetTableHeaders: extra.tableHeaders,
      sheetTableRows: extra.tableRows,
      sheetSummaryConfig: extra.summaryConfig,
    });
    XLSX.utils.book_append_sheet(
      workbook,
      extraWs,
      String(extra.sheetName || "Patients").slice(0, 31)
    );
  });

  XLSX.writeFile(workbook, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

export async function downloadPatientTableExcel(options) {
  const showDepartmentBreakdown = shouldShowDepartmentBreakdown(
    options?.fromDate,
    options?.toDate
  );
  return downloadTableExcelWithSummary({
    ...options,
    summaryConfig: {
      ...PATIENT_SUMMARY_CONFIG,
      skipDepartmentBreakdown: !showDepartmentBreakdown,
    },
  });
}

export async function downloadInvoiceTableExcel(options) {
  const showDepartmentBreakdown = shouldShowDepartmentBreakdown(
    options?.fromDate,
    options?.toDate
  );
  const baseSummaryConfig =
    options?.summaryVariant === "expense"
      ? EXPENSE_INVOICE_SUMMARY_CONFIG
      : INVOICE_SUMMARY_CONFIG;

  // Expenses use a type breakdown in the same slot; keep it for all ranges.
  // Income: hide department breakdown for monthly/yearly (multi-day) exports.
  const summaryConfig =
    options?.summaryVariant === "expense"
      ? baseSummaryConfig
      : {
          ...baseSummaryConfig,
          skipDepartmentBreakdown: !showDepartmentBreakdown,
        };

  const extraSheets = [];
  if (options?.patientSheet?.tableHeaders) {
    extraSheets.push({
      ...options.patientSheet,
      organizationName: options.organizationName,
      summaryConfig: {
        ...PATIENT_SUMMARY_CONFIG,
        skipDepartmentBreakdown: !showDepartmentBreakdown,
      },
    });
  }

  return downloadTableExcelWithSummary({
    ...options,
    summaryConfig,
    extraSheets,
  });
}

export async function downloadMedicineInventoryTableExcel(options) {
  return downloadTableExcelWithSummary({
    ...options,
    summaryConfig: MEDICINE_INVENTORY_SUMMARY_CONFIG,
  });
}

export async function downloadPathInventoryTableExcel(options) {
  return downloadTableExcelWithSummary({
    ...options,
    summaryConfig: PATH_INVENTORY_SUMMARY_CONFIG,
  });
}

function buildStyledTableWorksheet({
  tableHeaders = [],
  tableRows = [],
  organizationName,
  title,
  columnWidthOverrides = {},
} = {}) {
  const resolvedOrg = organizationName || getOrganizationName(null) || "";
  const customTitle = (title || "").trim();
  const sheetTitle = customTitle
    ? resolvedOrg
      ? `${resolvedOrg} - ${customTitle}`
      : customTitle
    : resolvedOrg;

  const columnCount = Math.max(
    Array.isArray(tableHeaders) ? tableHeaders.length : 0,
    1
  );
  const rows = [];
  let titleRowIndex = null;
  const headerRows = [];

  if (sheetTitle) {
    titleRowIndex = rows.length;
    rows.push(padRow([sheetTitle], columnCount));
  }

  const tableHeaderRowIndex = rows.length;
  rows.push(padRow(tableHeaders || [], columnCount));
  headerRows.push(tableHeaderRowIndex);

  const tableDataStart = rows.length;
  (tableRows || []).forEach((values) => {
    rows.push(padRow(values, columnCount));
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const merges = [];
  if (titleRowIndex != null) {
    merges.push({
      s: { r: titleRowIndex, c: 0 },
      e: { r: titleRowIndex, c: columnCount - 1 },
    });
    setCellStyle(ws, titleRowIndex, 0, ORG_TITLE_STYLE, sheetTitle);
  }
  ws["!merges"] = merges;

  headerRows.forEach((rowIndex) => {
    for (let col = 0; col < columnCount; col += 1) {
      setCellStyle(ws, rowIndex, col, HEADER_STYLE, rows[rowIndex]?.[col] ?? "");
    }
  });

  for (let rowIndex = tableDataStart; rowIndex < rows.length; rowIndex += 1) {
    for (let col = 0; col < columnCount; col += 1) {
      setCellStyle(ws, rowIndex, col, DATA_STYLE, rows[rowIndex]?.[col] ?? "");
    }
  }

  const borderedRowIndices = [
    ...(titleRowIndex != null ? [titleRowIndex] : []),
    ...headerRows,
    ...Array.from(
      { length: Math.max(rows.length - tableDataStart, 0) },
      (_, index) => tableDataStart + index
    ),
  ];
  applyTableGridBorders(ws, borderedRowIndices, columnCount);
  // Skip merged title row so col 0 (often Supplier ID) is not sized to the org title.
  autoFitColumns(ws, rows, columnCount, {
    skipRowIndexes: titleRowIndex != null ? [titleRowIndex] : [],
  });

  Object.entries(columnWidthOverrides || {}).forEach(([colKey, width]) => {
    const colIndex = Number(colKey);
    if (
      !Number.isFinite(colIndex) ||
      colIndex < 0 ||
      colIndex >= columnCount ||
      width == null
    ) {
      return;
    }
    if (!ws["!cols"]) ws["!cols"] = [];
    ws["!cols"][colIndex] = {
      ...(ws["!cols"][colIndex] || {}),
      wch: Number(width),
    };
  });

  return ws;
}

/**
 * Shared styled Excel download used across the app:
 * optional org/title row, blue header, thin borders, autofit columns.
 */
export async function downloadStyledTableExcel({
  filename,
  sheetName = "Export",
  tableHeaders = [],
  tableRows = [],
  organizationName,
  title,
} = {}) {
  const ws = buildStyledTableWorksheet({
    tableHeaders,
    tableRows,
    organizationName,
    title,
  });

  const workbook = XLSX.utils.book_new();
  const safeSheetName = String(sheetName || "Export").slice(0, 31) || "Export";
  XLSX.utils.book_append_sheet(workbook, ws, safeSheetName);
  XLSX.writeFile(
    workbook,
    filename?.endsWith(".xlsx") ? filename : `${filename || "export"}.xlsx`
  );
}

export async function downloadSupplierDetailsTableExcel({
  filename,
  organizationName,
  sheets = [],
  tableHeaders,
  tableRows,
} = {}) {
  const resolvedSheets =
    Array.isArray(sheets) && sheets.length
      ? sheets
      : [
          {
            sheetName: "Supplier Details",
            title: "Supplier Details",
            tableHeaders,
            tableRows,
          },
        ];

  const workbook = XLSX.utils.book_new();
  const usedNames = new Set();

  resolvedSheets.forEach((sheet) => {
    const supplierIdHeaderIndex = Array.isArray(sheet.tableHeaders)
      ? sheet.tableHeaders.findIndex(
          (header) => String(header).trim().toLowerCase() === "supplier id"
        )
      : -1;
    const sheetOrgName =
      sheet.organizationName !== undefined
        ? sheet.organizationName
        : organizationName;
    const ws = buildStyledTableWorksheet({
      tableHeaders: sheet.tableHeaders,
      tableRows: sheet.tableRows,
      organizationName: sheetOrgName,
      title: sheet.title,
      // Keep Supplier ID narrow; autofit previously inflated it via the merged title.
      columnWidthOverrides:
        supplierIdHeaderIndex >= 0 ? { [supplierIdHeaderIndex]: 12 } : {},
    });

    let safeSheetName =
      String(sheet.sheetName || "Export")
        .replace(/[\\/?*[\]:]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 31) || "Export";
    if (usedNames.has(safeSheetName.toLowerCase())) {
      let index = 2;
      while (index < 1000) {
        const suffix = `-${index}`;
        const candidate = `${safeSheetName.slice(0, Math.max(31 - suffix.length, 1))}${suffix}`;
        if (!usedNames.has(candidate.toLowerCase())) {
          safeSheetName = candidate;
          break;
        }
        index += 1;
      }
    }
    usedNames.add(safeSheetName.toLowerCase());
    XLSX.utils.book_append_sheet(workbook, ws, safeSheetName);
  });

  XLSX.writeFile(
    workbook,
    filename?.endsWith(".xlsx") ? filename : `${filename || "export"}.xlsx`
  );
}
